import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { color, font, layout, mixin, radius, space, toneColor } from '../admin_classic_theme';
import LinkifiedText from '../popup/linkified_text';
import {
    PreviewStage,
    PreviewViewport,
    PreviewBar,
    PreviewBrand,
    PreviewCanvas,
    PopupPreview,
    PreviewImage,
    ImagePlaceholder,
    PreviewTitle,
    PreviewBody,
    PreviewAction
} from '../popup/popup_settings.styles';

const emptyForm = () => ({
    id: null,
    event_code: '',
    name: '',
    dedup_scope: 'LIFETIME',
    starts_at: '',
    ends_at: '',
    status: 'DRAFT',
    rewards: {
        USER: {
            enabled: true,
            gift_goods_code: '',
            gift_mms_title: '가입축하',
            gift_mms_message: '에어컨알지 가입을 축하드립니다.',
            max_issues: 100
        },
        MANAGER: {
            enabled: true,
            discount_rate: 30,
            benefit_expires_at: ''
        }
    }
});

const CONTENT_TYPE_OPTIONS = [
    ['TEXT', '텍스트'],
    ['IMAGE', '이미지'],
    ['IMAGE_TEXT', '이미지 + 텍스트']
];

const toLocal = (value) => value ? String(value).slice(0, 16) : '';
const toApi = (value) => value ? `${value}:00+09:00` : '';
const money = (value) => `${Number(value || 0).toLocaleString('ko-KR')}원`;

function eventToForm(event) {
    if (!event) return emptyForm();
    return {
        id: event.id,
        event_code: event.event_code,
        name: event.name,
        dedup_scope: event.dedup_scope,
        starts_at: toLocal(event.starts_at),
        ends_at: toLocal(event.ends_at),
        status: event.status,
        rewards: {
            USER: {
                enabled: event.rewards?.USER?.enabled === true || Number(event.rewards?.USER?.enabled) === 1,
                gift_goods_code: event.rewards?.USER?.gift_goods_code || '',
                gift_mms_title: event.rewards?.USER?.gift_mms_title || '',
                gift_mms_message: event.rewards?.USER?.gift_mms_message || '',
                max_issues: Number(event.rewards?.USER?.max_issues || 100)
            },
            MANAGER: {
                enabled: event.rewards?.MANAGER?.enabled === true || Number(event.rewards?.MANAGER?.enabled) === 1,
                discount_rate: Number(event.rewards?.MANAGER?.discount_rate || 30),
                benefit_expires_at: toLocal(event.rewards?.MANAGER?.benefit_expires_at)
            }
        }
    };
}

function statusMeta(status) {
    return {
        DRAFT: ['초안', 'neutral'],
        PUBLISHED: ['게시', 'live'],
        PAUSED: ['일시중지', 'warning'],
        ENDED: ['종료', 'ended']
    }[status] || [status, 'neutral'];
}

// embedded: 팝업관리 "가입혜택관리" 탭에 임베드될 때는 자체 Shell·페이지 Header 없이 렌더한다.
// popupSlot: 이벤트 팝업(PRE_LOGIN_EVENT 슬롯)의 폼 상태·저장 함수를 부모에게서 받는 계약.
//   이 화면의 저장 버튼 하나가 팝업 콘텐츠와 이벤트를 함께 저장해야 해서, 팝업 상태는 부모가
//   들고 편집 UI만 이 카드 안에 둔다. null이면(팝업 권한 없음·단독 사용) 팝업 섹션을 렌더하지 않는다.
// onEventsChanged: 저장·상태 전이가 성공하면 호출한다 — 이벤트가 팝업의 노출·기간을 바꾸므로
//   임베드한 부모가 팝업 상태를 재동기화할 수 있어야 한다(단독 사용 시 기본 no-op).
function BenefitEvents({ embedded = false, popupSlot = null, onEventsChanged = () => {} }) {
    const [events, setEvents] = useState([]);
    const [form, setForm] = useState(emptyForm());
    const [participants, setParticipants] = useState([]);
    const [deliveries, setDeliveries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    const loadEvents = async (selectedId) => {
        setLoading(true);
        try {
            const response = await axios.get('/api/admin/benefit-events');
            const loaded = response.data.events || [];
            setEvents(loaded);
            const selected = loaded.find((event) => Number(event.id) === Number(selectedId || form.id)) || loaded[0];
            if (selected) setForm(eventToForm(selected));
        } catch (loadError) {
            setError(loadError.response?.data?.message || '가입혜택 이벤트를 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadEvents(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const selectedEvent = events.find((event) => Number(event.id) === Number(form.id));
    const participantCount = Number(selectedEvent?.user_count || 0) + Number(selectedEvent?.manager_count || 0);
    const editable = !form.id || (form.status !== 'ENDED' && participantCount === 0);
    const managerPayable = useMemo(
        () => Math.floor(110000 * (100 - Number(form.rewards.MANAGER.discount_rate || 0)) / 100),
        [form.rewards.MANAGER.discount_rate]
    );

    // 카드가 길어 저장 버튼이 화면 아래에 있는데 결과 메시지는 카드 위에 붙는다 — 스크롤 위치에
    // 따라 저장 성공·실패가 통째로 안 보인다. 레거시 관리자 페이지들처럼(total_biling.js 등)
    // alert로도 알려 스크롤과 무관하게 결과가 전달되게 한다.
    const announce = (message, isError = false) => {
        setError(isError ? message : '');
        setNotice(isError ? '' : message);
        window.alert(message);
    };

    const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
    const updateReward = (audience, field, value) => setForm((current) => ({
        ...current,
        rewards: { ...current.rewards, [audience]: { ...current.rewards[audience], [field]: value } }
    }));

    const payload = () => ({
        event_code: form.event_code,
        name: form.name,
        dedup_scope: form.dedup_scope,
        starts_at: toApi(form.starts_at),
        ends_at: toApi(form.ends_at),
        rewards: {
            USER: form.rewards.USER,
            MANAGER: {
                ...form.rewards.MANAGER,
                benefit_expires_at: toApi(form.rewards.MANAGER.benefit_expires_at)
            }
        }
    });

    const selectEvent = (eventId) => {
        const selected = events.find((event) => Number(event.id) === Number(eventId));
        setForm(selected ? eventToForm(selected) : emptyForm());
        setParticipants([]);
        setDeliveries([]);
        setError('');
        setNotice('');
    };

    const popupForm = popupSlot?.form || null;
    const slotOwnerId = popupForm?.managed_by_event_id || null;
    const slotOwnedBySelected = Boolean(slotOwnerId) && Number(slotOwnerId) === Number(form.id);
    // 팝업 슬롯은 전역 1행이라 저장은 항상 즉시 반영이다. 다른 이벤트가 점유 중이면 그 이벤트의
    // 라이브 팝업 콘텐츠를 덮어쓰게 되므로(서버는 기간·노출만 보호한다 — popupSettingsHandler.js:364)
    // 편집 자체를 잠근다. "게시하면 적용된다"는 안내로는 이 즉시성을 막지 못한다.
    const popupLocked = Boolean(slotOwnerId) && !slotOwnedBySelected;
    const popupNeedsText = popupForm?.content_type === 'TEXT' || popupForm?.content_type === 'IMAGE_TEXT';
    const popupNeedsImage = popupForm?.content_type === 'IMAGE' || popupForm?.content_type === 'IMAGE_TEXT';
    const popupDirty = Boolean(popupSlot?.dirty) && !popupLocked;

    // 서버는 게시 직전에 팝업 콘텐츠 완성을 검사한다(benefitEventHandler.js:636). 그 검사는
    // 저장된 DB 값을 보므로, 내용이 비었거나 미저장 변경이 남아 있으면 게시가 반드시 거절된다 —
    // 서버에 갔다가 거절당하기 전에 화면에서 사유를 알리고 막는다.
    const publishBlockReason = !popupSlot || popupLocked
        ? ''
        : popupSlot.contentError
            ? '게시하려면 이벤트 팝업의 본문 또는 이미지를 입력하고 저장해야 합니다.'
            : popupDirty
                ? '이벤트 팝업의 변경 내용을 먼저 저장해 주세요.'
                : '';

    // 저장은 팝업 콘텐츠 → 이벤트 순서다. 게시 게이트가 팝업 콘텐츠 완성을 선행 조건으로 보고
    // (benefitEventHandler.js:636), 게시 중 이벤트의 PUT은 팝업 기간을 재동기화하므로
    // (:578) 순서를 뒤집으면 팝업 version이 두 번 오른다.
    const save = async () => {
        if (!form.starts_at || !form.ends_at) {
            announce('이벤트 시작·종료 일시를 먼저 입력해 주세요.', true);
            return;
        }
        if (popupDirty && popupSlot.contentError) {
            announce(`이벤트 팝업 — ${popupSlot.contentError}`, true);
            return;
        }

        setSaving(true); setError(''); setNotice('');
        let popupSaved = false;
        try {
            if (popupDirty) {
                await popupSlot.save({ startsAt: form.starts_at, endsAt: form.ends_at });
                popupSaved = true;
            }
        } catch (popupError) {
            announce(popupError.response?.data?.message || '이벤트 팝업 내용을 저장하지 못했습니다.', true);
            setSaving(false);
            return;
        }

        if (!editable) {
            setSaving(false);
            if (popupSaved) {
                announce('이벤트 팝업 내용을 저장했습니다.');
                onEventsChanged();
            }
            return;
        }

        try {
            const response = form.id
                ? await axios.put(`/api/admin/benefit-events/${form.id}`, payload())
                : await axios.post('/api/admin/benefit-events', payload());
            const eventMessage = form.id ? '이벤트 설정을 저장했습니다.' : '새 이벤트를 생성했습니다.';
            announce(popupSaved ? `이벤트 팝업 내용과 ${eventMessage}` : eventMessage);
            await loadEvents(response.data.event.id);
            onEventsChanged();
        } catch (saveError) {
            const message = saveError.response?.data?.message || '이벤트를 저장하지 못했습니다.';
            announce(popupSaved
                ? `이벤트 팝업 내용은 저장했지만 이벤트 저장에 실패했습니다 — ${message}`
                : message, true);
        } finally { setSaving(false); }
    };

    const transition = async (action) => {
        if (!form.id) return;
        if (!window.confirm(`이 이벤트를 ${action === 'publish' ? '게시' : action === 'pause' ? '일시중지' : action === 'resume' ? '재개' : '종료'}하시겠습니까?`)) return;
        setSaving(true); setError(''); setNotice('');
        try {
            await axios.post(`/api/admin/benefit-events/${form.id}/${action}`);
            announce('이벤트 상태와 이벤트 팝업 노출을 함께 변경했습니다.');
            await loadEvents(form.id);
            onEventsChanged();
        } catch (transitionError) {
            announce(transitionError.response?.data?.message || '이벤트 상태를 변경하지 못했습니다.', true);
        } finally { setSaving(false); }
    };

    const loadOperations = async () => {
        if (!form.id) return;
        try {
            const [participantResponse, deliveryResponse] = await Promise.all([
                axios.get(`/api/admin/benefit-events/${form.id}/participants`),
                axios.get('/api/admin/benefit-deliveries', { params: { event_id: form.id } })
            ]);
            setParticipants(participantResponse.data.participants || []);
            setDeliveries(deliveryResponse.data.deliveries || []);
        } catch (participantError) {
            setError(participantError.response?.data?.message || '참여 내역을 불러오지 못했습니다.');
        }
    };

    const deliveryAction = async (delivery, action) => {
        setSaving(true); setError(''); setNotice('');
        try {
            await axios.post(`/api/admin/benefit-deliveries/${delivery.id}/${action}`);
            setNotice(action === 'retry' ? '재발송 작업을 접수했습니다.' : '기프티쇼 상태를 대사했습니다.');
            await loadOperations();
        } catch (actionError) {
            setError(actionError.response?.data?.message || '쿠폰 작업을 처리하지 못했습니다.');
        } finally { setSaving(false); }
    };

    const startNewEvent = () => {
        setForm(emptyForm());
        setParticipants([]);
        setDeliveries([]);
        setError('');
        setNotice('');
    };

    if (loading && events.length === 0) {
        const panel = <Panel>가입혜택 이벤트를 불러오는 중입니다.</Panel>;
        return embedded ? panel : <Shell>{panel}</Shell>;
    }

    const [statusLabel, statusTone] = statusMeta(form.status);
    const saveDisabled = saving || (!editable && !popupDirty);
    const page = (
        <>
            {!embedded && (
                <Header>
                    <div><h1>가입혜택관리</h1><p>신규가입 조건, 고객 커피 쿠폰과 매니저 교육 할인 정책을 한 곳에서 관리합니다.</p></div>
                </Header>
            )}
            {(error || notice) && <Message $error={Boolean(error)} role={error ? 'alert' : 'status'}>{error || notice}</Message>}
            <Card>
                <Topline>
                    <Picker>
                        <label htmlFor="benefit-event-select">이벤트</label>
                        <select
                            id="benefit-event-select"
                            value={form.id || ''}
                            onChange={(e) => selectEvent(e.target.value)}
                        >
                            {!form.id && <option value="">새 이벤트 (미저장)</option>}
                            {events.map((event) => {
                                const [label] = statusMeta(event.status);
                                return <option key={event.id} value={event.id}>{`${event.name} · ${event.event_code} · ${label}`}</option>;
                            })}
                        </select>
                        <OutlineButton type="button" onClick={startNewEvent}>새 이벤트</OutlineButton>
                    </Picker>
                    <Actions>
                        <Status $tone={statusTone}>{statusLabel}</Status>
                        {form.id && form.status === 'DRAFT' && <OutlineButton disabled={saving || Boolean(publishBlockReason)} title={publishBlockReason || undefined} onClick={() => transition('publish')}>게시</OutlineButton>}
                        {form.status === 'PUBLISHED' && <OutlineButton disabled={saving} onClick={() => transition('pause')}>일시중지</OutlineButton>}
                        {form.status === 'PAUSED' && <OutlineButton disabled={saving || Boolean(publishBlockReason)} title={publishBlockReason || undefined} onClick={() => transition('resume')}>재개</OutlineButton>}
                        {form.id && !['DRAFT', 'ENDED'].includes(form.status) && <DangerButton onClick={() => transition('end')}>종료</DangerButton>}
                    </Actions>
                </Topline>

                <Section>
                    <SectionTitle><div><h2>이벤트 조건</h2><p>카카오 신규가입 시점과 이벤트 팝업 노출 기간을 함께 결정합니다. 일반 공지("로그인 전") 슬롯은 그대로 유지됩니다.</p></div></SectionTitle>
                    <Grid>
                        <Field><label>이벤트 코드</label><input disabled={!editable} value={form.event_code} onChange={(e) => update('event_code', e.target.value.toUpperCase())} placeholder="WELCOME_2026" /></Field>
                        <Field><label>이벤트명</label><input disabled={!editable} value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="신규가입 웰컴 이벤트" /></Field>
                        <Field><label>시작 일시</label><input disabled={!editable} type="datetime-local" value={form.starts_at} onChange={(e) => update('starts_at', e.target.value)} /></Field>
                        <Field><label>종료 일시</label><input disabled={!editable} type="datetime-local" value={form.ends_at} onChange={(e) => update('ends_at', e.target.value)} /></Field>
                        <Field $wide><label>중복 정책</label><select disabled={!editable} value={form.dedup_scope} onChange={(e) => update('dedup_scope', e.target.value)}><option value="LIFETIME">역할별 평생 1회</option><option value="CAMPAIGN">캠페인별 1회</option></select><small>전화번호와 카카오 가입 식별값의 HMAC 해시로 탈퇴·재가입을 확인합니다.</small></Field>
                    </Grid>
                </Section>

                <Section>
                    <SectionTitle><div><h2>고객 가입 혜택</h2><p>가입 완료 후 기프티쇼 비즈 MMS 발송 작업을 자동 생성합니다.</p></div><Toggle><input type="checkbox" disabled={!editable} checked={form.rewards.USER.enabled} onChange={(e) => updateReward('USER', 'enabled', e.target.checked)} /><i aria-hidden="true"><span /></i><b>사용</b></Toggle></SectionTitle>
                    <Grid $disabled={!form.rewards.USER.enabled}>
                        <Field><label>기프티쇼 상품코드</label><input disabled={!editable || !form.rewards.USER.enabled} value={form.rewards.USER.gift_goods_code} onChange={(e) => updateReward('USER', 'gift_goods_code', e.target.value)} /></Field>
                        <Field><label>최대 발급 수량</label><input disabled={!editable || !form.rewards.USER.enabled} type="number" min="1" value={form.rewards.USER.max_issues} onChange={(e) => updateReward('USER', 'max_issues', Number(e.target.value))} /></Field>
                        <Field><label>MMS 제목 · 최대 10자</label><input disabled={!editable || !form.rewards.USER.enabled} maxLength="10" value={form.rewards.USER.gift_mms_title} onChange={(e) => updateReward('USER', 'gift_mms_title', e.target.value)} /></Field>
                        <Field $wide><label>MMS 메시지</label><textarea disabled={!editable || !form.rewards.USER.enabled} value={form.rewards.USER.gift_mms_message} onChange={(e) => updateReward('USER', 'gift_mms_message', e.target.value)} /></Field>
                    </Grid>
                </Section>

                <Section>
                    <SectionTitle><div><h2>매니저 가입 혜택</h2><p>기간 내 가입 요청자에게 만료일까지 최초 교육 결제 할인을 부여합니다.</p></div><Toggle><input type="checkbox" disabled={!editable} checked={form.rewards.MANAGER.enabled} onChange={(e) => updateReward('MANAGER', 'enabled', e.target.checked)} /><i aria-hidden="true"><span /></i><b>사용</b></Toggle></SectionTitle>
                    <Grid $disabled={!form.rewards.MANAGER.enabled}>
                        <Field><label>할인율</label><input disabled={!editable || !form.rewards.MANAGER.enabled} type="number" min="1" max="99" value={form.rewards.MANAGER.discount_rate} onChange={(e) => updateReward('MANAGER', 'discount_rate', Number(e.target.value))} /><small>정가 110,000원 → {money(managerPayable)}</small></Field>
                        <Field><label>혜택 만료 일시</label><input disabled={!editable || !form.rewards.MANAGER.enabled} type="datetime-local" value={form.rewards.MANAGER.benefit_expires_at} onChange={(e) => updateReward('MANAGER', 'benefit_expires_at', e.target.value)} /></Field>
                    </Grid>
                </Section>

                {popupSlot && (
                    <Section>
                        <SectionTitle><div><h2>이벤트 팝업</h2><p>노출 기간과 노출 여부는 위 이벤트 기간·게시 상태를 따릅니다. 여기서는 앱에 보일 내용만 작성합니다.</p></div></SectionTitle>
                        <SlotNote role="status" $warning={popupLocked}>
                            {popupLocked
                                ? `현재 이벤트 #${slotOwnerId}가 이벤트 팝업을 사용 중입니다. 저장하면 그 이벤트의 노출 중인 내용이 바뀌므로, 그 이벤트를 종료한 뒤에 수정할 수 있습니다.`
                                : slotOwnedBySelected
                                    ? '이 이벤트가 로그인 전 이벤트 팝업을 사용 중입니다. 저장하면 앱에 바로 반영됩니다.'
                                    : '이 슬롯을 사용 중인 이벤트가 없습니다. 저장한 내용은 슬롯에 바로 반영되고, 이 이벤트를 게시하면 앱에 노출됩니다.'}
                        </SlotNote>
                        {publishBlockReason && (
                            <SlotNote role="status" $warning>{publishBlockReason}</SlotNote>
                        )}
                        <PopupGrid>
                            <div>
                                <Grid>
                                    <Field>
                                        <label htmlFor="event-popup-type">팝업 형식</label>
                                        <select
                                            id="event-popup-type"
                                            disabled={popupLocked}
                                            value={popupForm.content_type}
                                            onChange={(e) => popupSlot.updateField('content_type', e.target.value)}
                                        >
                                            {CONTENT_TYPE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                                        </select>
                                    </Field>
                                    <Field>
                                        <label htmlFor="event-popup-title">제목 (선택)</label>
                                        <input
                                            id="event-popup-title"
                                            disabled={popupLocked}
                                            maxLength="200"
                                            value={popupForm.title}
                                            onChange={(e) => popupSlot.updateField('title', e.target.value)}
                                            placeholder="팝업 제목을 입력해 주세요."
                                        />
                                    </Field>
                                    {popupNeedsText && (
                                        <Field $wide>
                                            <label htmlFor="event-popup-body">본문</label>
                                            <textarea
                                                id="event-popup-body"
                                                disabled={popupLocked}
                                                maxLength="5000"
                                                value={popupForm.body}
                                                onChange={(e) => popupSlot.updateField('body', e.target.value)}
                                                placeholder={'본문을 입력해 주세요.\nhttps:// 로 시작하는 주소는 앱에서 자동으로 링크 처리됩니다.'}
                                            />
                                            <small>{popupForm.body.length.toLocaleString('ko-KR')} / 5,000자 · HTTP/HTTPS 주소는 앱에서 링크로 열립니다.</small>
                                        </Field>
                                    )}
                                    {popupNeedsImage && (
                                        <Field $wide>
                                            <label htmlFor="event-popup-image">이미지</label>
                                            <input
                                                id="event-popup-image"
                                                ref={popupSlot.fileInputRef}
                                                disabled={popupLocked}
                                                type="file"
                                                accept="image/jpeg,image/png,image/gif,image/webp"
                                                onChange={popupSlot.handleImageChange}
                                            />
                                            <small>
                                                {popupSlot.selectedImage
                                                    ? `${popupSlot.selectedImage.name} · 저장 시 새 이미지로 교체됩니다.`
                                                    : popupForm.image_url
                                                        ? '기존 이미지를 사용 중입니다. 새 파일을 선택하지 않으면 그대로 유지됩니다.'
                                                        : 'JPEG, PNG, GIF, WebP · 최대 10MB'}
                                            </small>
                                        </Field>
                                    )}
                                </Grid>
                            </div>
                            <PreviewColumn>
                                <PreviewHeading>앱 미리보기</PreviewHeading>
                                <PreviewStage $compact>
                                    <PreviewViewport $compact>
                                        <PreviewBar>
                                            <PreviewBrand><i /> AirconRG</PreviewBrand>
                                            <span>가입혜택 팝업</span>
                                        </PreviewBar>
                                        <PreviewCanvas $compact>
                                            <PopupPreview>
                                                {popupNeedsImage && (popupSlot.imageSource
                                                    ? <PreviewImage src={popupSlot.imageSource} alt="팝업 미리보기" />
                                                    : <ImagePlaceholder>이미지를 선택하면<br />이곳에 표시됩니다.</ImagePlaceholder>)}
                                                {popupForm.title && <PreviewTitle>{popupForm.title}</PreviewTitle>}
                                                {popupNeedsText && (
                                                    <PreviewBody><LinkifiedText>{popupForm.body || '본문 미리보기'}</LinkifiedText></PreviewBody>
                                                )}
                                                <PreviewAction type="button">확인</PreviewAction>
                                            </PopupPreview>
                                        </PreviewCanvas>
                                    </PreviewViewport>
                                </PreviewStage>
                            </PreviewColumn>
                        </PopupGrid>
                    </Section>
                )}

                <Footer>
                    <div>{!editable && (popupSlot
                        ? '참여자가 발생한 이벤트의 조건은 잠겨 있습니다. 팝업 내용만 저장할 수 있습니다.'
                        : '참여자가 발생한 이벤트의 경제 조건은 잠겨 있습니다.')}</div>
                    <PrimaryButton disabled={saveDisabled} onClick={save}>
                        {saving ? '처리 중…' : !editable ? '팝업 내용 저장' : form.id ? '설정 저장' : '이벤트 생성'}
                    </PrimaryButton>
                </Footer>

                {form.id && (
                    <Section>
                        <SectionTitle><div><h2>참여 및 성과</h2><p>고객 {selectedEvent?.user_count || 0}명 · 매니저 {selectedEvent?.manager_count || 0}명 · 쿠폰 발송 {selectedEvent?.sent_count || 0}건</p></div><OutlineButton onClick={loadOperations}>내역 조회</OutlineButton></SectionTitle>
                        {participants.length > 0 && <TableScroll><Table><thead><tr><th>대상</th><th>계정</th><th>상태</th><th>가입시각</th><th>혜택</th></tr></thead><tbody>{participants.map((item) => <tr key={item.id}><td>{item.audience === 'USER' ? '고객' : '매니저'}</td><td>{item.subject_id}</td><td>{item.delivery_state || item.entitlement_state || item.state}</td><td>{new Date(item.qualified_at).toLocaleString('ko-KR')}</td><td>{item.audience === 'USER' ? item.provider_order_no || '-' : `${item.discount_rate || 0}% · ${item.expires_at ? new Date(item.expires_at).toLocaleString('ko-KR') : '-'}`}</td></tr>)}</tbody></Table></TableScroll>}
                        {deliveries.length > 0 && <><Subheading>기프티쇼 발송 운영</Subheading><SlotNote role="status">기프티쇼 발송은 1분마다 이루어집니다 — 방금 접수된 건이 상태 PENDING · 응답 &apos;-&apos;로 보이는 것은 정상이며, 1분 안에 갱신됩니다.</SlotNote><TableScroll><Table><thead><tr><th>거래 ID</th><th>수신자</th><th>상태</th><th>응답</th><th>재시도</th><th>작업</th></tr></thead><tbody>{deliveries.map((item) => <tr key={item.id}><td><code>{item.tr_id}</code></td><td>{item.recipient_phone_masked}</td><td>{item.state}</td><td>{item.provider_code === '0000' ? 'Success' : (item.provider_code || '-')} {item.provider_message || ''}</td><td>{item.retry_count}</td><td><TableActions>{item.state === 'FAILED' && <MiniButton disabled={saving} onClick={() => deliveryAction(item, 'retry')}>재시도</MiniButton>}{item.state === 'UNKNOWN' && <MiniButton disabled={saving} onClick={() => deliveryAction(item, 'reconcile')}>상태조회</MiniButton>}</TableActions></td></tr>)}</tbody></Table></TableScroll></>}
                    </Section>
                )}
            </Card>
        </>
    );
    return embedded ? page : <Shell>{page}</Shell>;
}

const Shell = styled.main`
    min-height: calc(100vh - 90px);
    padding: ${layout.pagePadding};
    background: ${color.page};
    color: ${color.text};

    @media (max-width: 720px) { padding: ${layout.pagePaddingSmall}; }
`;
const Header = styled.header`
    max-width: ${layout.maxWidth};
    margin: 0 auto ${space.lg};

    > div { min-width: 0; }
    h1 { margin: 0; color: ${color.textStrong}; ${font.pageTitle}; }
    p { max-width: 680px; margin: ${space.sm} 0 0; color: ${color.textMuted}; ${font.caption}; }
`;
const Card = styled.div`
    ${mixin.panel};
    max-width: ${layout.maxWidth};
    margin: 0 auto;
    overflow: hidden;
`;
const Topline = styled.div`
    min-height: 60px;
    padding: ${space.md} ${space.xl};
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${space.lg};
    border-bottom: 1px solid ${color.line};
    background: ${color.surfaceMuted};

    @media (max-width: 720px) { align-items: stretch; flex-direction: column; }
`;
const Picker = styled.div`
    min-width: 0;
    display: flex;
    align-items: center;
    gap: ${space.sm};

    label { flex: 0 0 auto; color: ${color.textMuted}; ${font.label}; }
    select {
        ${mixin.field};
        width: auto;
        min-width: 240px;
        max-width: 380px;
        height: 40px;
        min-height: 40px;
    }

    @media (max-width: 560px) {
        flex-wrap: wrap;
        select { min-width: 0; flex: 1 1 100%; }
    }
`;
const Section = styled.section`
    padding: ${space.xl};
    ${mixin.hairlineTop};

    &:first-of-type { border-top: 0; }
    @media (max-width: 720px) { padding: ${space.lg}; }
`;
const SectionTitle = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${space.md};
    margin-bottom: ${space.lg};

    > div { flex: 1; min-width: 0; }
    h2 { margin: 0; color: ${color.textStrong}; ${font.sectionTitle}; }
    p { margin: 5px 0 0; color: ${color.textFaint}; ${font.caption}; }

    @media (max-width: 580px) { flex-wrap: wrap; }
`;
const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${space.lg};
    opacity: ${p => p.$disabled ? 0.55 : 1};
    transition: opacity 140ms ease;

    @media (max-width: 700px) { grid-template-columns: 1fr; }
`;
const Field = styled.div`
    grid-column: ${p => p.$wide ? '1/-1' : 'auto'};
    display: flex;
    flex-direction: column;
    gap: ${space.sm};

    label { color: ${color.textMuted}; ${font.label}; }
    input, select, textarea { ${mixin.field}; }
    input[type='file'] { padding: 9px 12px; }
    textarea { min-height: 100px; padding: 10px 12px; resize: vertical; line-height: 1.65; }
    small { color: ${color.textFaint}; font-size: 0.75rem; line-height: 1.5; }
`;
const SlotNote = styled.p`
    margin: 0 0 ${space.lg};
    padding: ${space.sm} ${space.md};
    border: 1px solid ${p => p.$warning ? color.warning : color.line};
    border-radius: ${radius.field};
    background: ${color.surfaceMuted};
    color: ${p => p.$warning ? color.warning : color.textMuted};
    ${font.caption};
    font-weight: ${p => p.$warning ? 600 : 400};
`;
const PopupGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 300px;
    gap: ${space.xl};
    align-items: start;

    @media (max-width: 900px) { grid-template-columns: 1fr; }
`;
const PreviewColumn = styled.div`
    min-width: 0;
`;
const PreviewHeading = styled.h3`
    margin: 0 0 ${space.sm};
    color: ${color.textMuted};
    ${font.label};
`;
const Status = styled.span`
    ${mixin.statusChip};
    color: ${p => toneColor(p.$tone)};
`;
const Actions = styled.div`display: flex; align-items: center; gap: ${space.sm}; flex-wrap: wrap;`;
const Footer = styled.div`
    padding: ${space.lg} ${space.xl};
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${space.lg};
    ${mixin.hairlineTop};
    background: ${color.surfaceMuted};
    color: ${color.textMuted};
    font-size: 0.8rem;

    @media (max-width: 620px) { align-items: stretch; flex-direction: column; }
`;
const BaseButton = styled.button`
    ${mixin.buttonBase};
`;
const PrimaryButton = styled(BaseButton)`
    ${mixin.buttonPrimary};
    min-width: 112px;
`;
const OutlineButton = styled(BaseButton)`
    ${mixin.buttonGhost};
`;
const DangerButton = styled(BaseButton)`
    ${mixin.buttonDanger};
`;
const Message = styled.div`
    max-width: ${layout.maxWidth};
    margin: 0 auto ${space.lg};
    padding: ${space.md} ${space.lg};
    border: 1px solid ${p => p.$error ? color.danger : color.line};
    border-radius: ${radius.field};
    background: ${color.surface};
    color: ${p => p.$error ? color.danger : color.text};
    ${font.caption};
    font-weight: 600;
`;
const Toggle = styled.label`
    position: relative;
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: ${space.sm};
    color: ${color.text};
    font-size: 0.8rem;
    white-space: nowrap;
    cursor: pointer;

    input { position: absolute; inset: 0; z-index: 1; width: 100%; height: 100%; margin: 0; opacity: 0; cursor: pointer; }
    > i { position: relative; width: 44px; height: 25px; border: 1px solid ${color.lineStrong}; border-radius: ${radius.pill}; background: ${color.surfaceMuted}; transition: background 140ms ease, border-color 140ms ease; }
    > i span { position: absolute; top: 2px; left: 2px; width: 19px; height: 19px; border-radius: 50%; background: ${color.lineStrong}; transition: left 140ms ease, background 140ms ease; }
    input:checked + i { border-color: ${color.accent}; background: ${color.accent}; }
    input:checked + i span { left: 21px; background: ${color.onAccent}; }
    input:focus-visible + i { outline: 2px solid ${color.accent}; outline-offset: 2px; }
    input:disabled { cursor: not-allowed; }
    input:disabled + i, input:disabled ~ b { opacity: 0.55; cursor: not-allowed; }
    b { font-size: 0.8rem; font-weight: 700; }
`;
const TableScroll = styled.div`
    width: 100%;
    overflow-x: auto;
    border: 1px solid ${color.line};
    border-radius: ${radius.field};
`;
const Table = styled.table`
    ${mixin.table};
    min-width: 720px;

    tbody tr:hover { background: ${color.surfaceMuted}; }
`;
const Subheading = styled.h3`
    margin: ${space.xl} 0 ${space.md};
    color: ${color.textStrong};
    ${font.label};
    font-size: 0.95rem;
`;
const TableActions = styled.div`display: flex; gap: 6px; flex-wrap: wrap;`;
const MiniButton = styled.button`
    ${mixin.buttonBase};
    ${mixin.buttonGhost};
    height: 28px;
    padding: 0 ${space.sm};
    font-size: 0.75rem;
`;
const Panel = styled.div`
    ${mixin.panel};
    max-width: 700px;
    margin: 56px auto;
    padding: ${space.xl};
    color: ${color.textMuted};
    ${font.body};
`;

export default BenefitEvents;
